/**
 * Reports the lifecycle of one overlay.
 *
 * `Common/Modal` and `Common/Dialog` each call this once, which is what makes
 * every overlay in the app instrumented without any feature opting in.
 *
 * ## Why the close is detected from `open`, not from `onClose`
 *
 * MUI fires `onClose` only for a backdrop click and Escape, and our close (X)
 * calls it directly. The path that matters most — the caller finishes its work
 * and flips `open` to false — never touches it. Watching the `open` transition
 * catches all of them, plus the case no handler can see: the component
 * unmounting while still open, which is what a route change away from an open
 * overlay looks like. Without that, every funnel would leak.
 *
 * `reportClose` exists so the component can say *how* it was closed before the
 * transition happens. Anything the effect sees without a report is
 * `programmatic` — see `ModalOutcome` for why that is named after the mechanism
 * rather than presented as a success.
 *
 * ## Not exported from ./index
 *
 * This module imports `track` from the barrel, so re-exporting it there would
 * close an import cycle. Import it by path.
 */

import { useCallback, useEffect, useRef } from "react";
import { useParams } from "react-router";

import { track } from "./index";
import { buildModalClosedEvent, buildModalOpenedEvent } from "./modalEvents";
import type { ModalAnalyticsId } from "./modalIds";
import type { ModalCloseVia, ModalOutcome, ModalSurface } from "./types";

interface UseModalAnalyticsInput {
  analyticsId: ModalAnalyticsId;
  surface: ModalSurface;
  open: boolean;
}

interface UseModalAnalyticsResult {
  /** Record how this overlay is being closed. Call it synchronously, before
   *  handing control to the caller's handler — the close event is built from
   *  whatever was reported by the time `open` flips. */
  reportClose: (outcome: ModalOutcome, via: ModalCloseVia) => void;
}

const toId = (value: string | undefined): number | null => {
  if (!value) return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

export const useModalAnalytics = ({
  analyticsId,
  surface,
  open,
}: UseModalAnalyticsInput): UseModalAnalyticsResult => {
  // Same source as page_view (see pageView.ts) so the two agree. Outside a
  // router — Storybook, a bare component test — useParams returns an empty
  // object rather than throwing, so this needs no guard.
  const params = useParams();

  const openedAtRef = useRef<number | null>(null);
  const pendingCloseRef = useRef<{ outcome: ModalOutcome; via: ModalCloseVia } | null>(null);

  // The effect below runs only on an `open` transition, but its cleanup can fire
  // long afterwards — on unmount — by which time a re-render may have changed
  // the id or the route. Reading these through a ref keeps the close event
  // describing the overlay that actually closed, without making every prop
  // change re-run the effect and emit a spurious open/close pair.
  const latestRef = useRef({ analyticsId, surface, params });
  latestRef.current = { analyticsId, surface, params };

  const reportClose = useCallback((outcome: ModalOutcome, via: ModalCloseVia) => {
    pendingCloseRef.current = { outcome, via };
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const scope = () => ({
      analyticsId: latestRef.current.analyticsId,
      surface: latestRef.current.surface,
      hubId: toId(latestRef.current.params.h_id),
      projectId: toId(latestRef.current.params.p_id),
    });

    // Monotonic, so a clock change mid-dialog cannot produce a negative
    // duration. `occurred_at` still comes from the wall clock.
    openedAtRef.current = window.performance.now();
    pendingCloseRef.current = null;

    track(buildModalOpenedEvent(scope()));

    return () => {
      const { outcome, via } = pendingCloseRef.current ?? {
        outcome: "programmatic" as const,
        via: "programmatic" as const,
      };

      track(
        buildModalClosedEvent({
          ...scope(),
          outcome,
          via,
          msOpen: window.performance.now() - (openedAtRef.current ?? window.performance.now()),
        }),
      );

      pendingCloseRef.current = null;
      openedAtRef.current = null;
    };
  }, [open]);

  return { reportClose };
};
